import { IconProps } from '@chakra-ui/react'
import React, { ReactNode, useMemo, useState } from 'react'
import { TextButton } from '~/components/shared/TextButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { hasValue } from '~/utils/common'
import { castRef } from '~/utils/refUtils'

import { TextButtonVariants } from '../componentStyles/TextButton.style'
import { Menu, MenuOptions, MenuPositioningWrapper } from './Menu'
import { MenuItem } from './MenuItem'

export type TextButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type DropdownTextButtonProps<TValue> = {
  children?: ReactNode
  tooltipLabel?: string
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  onButtonClick?: () => void
  items: TextButtonMenuItem<TValue>[]
  selectedIndex?: number
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => boolean | undefined
  menuOptions?: MenuOptions
  placeholder?: string
  variant?: TextButtonVariants
  header?: ReactNode
  hoverChanged?: (hovered: boolean) => void
  leftIconProps?: IconProps
  rightIconProps?: IconProps
}

export const DropdownTextButton = <
  TValue extends string | number | object | never,
>({
  children,
  placeholder,
  tooltipLabel,
  leftIcon,
  rightIcon,
  onButtonClick,
  items,
  selectedIndex,
  onSelect,
  onMenuOpenChange,
  menuOptions,
  variant,
  header,
  hoverChanged,
  leftIconProps,
  rightIconProps,
}: DropdownTextButtonProps<TValue>) => {
  const [internalIndex, setInternalIndex] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (menuOpen) {
        setMenuOpen(false)
        onMenuOpenChange?.(false)
      }
    },
  })

  const buttonLabel = useMemo(() => {
    const index = selectedIndex ?? internalIndex
    const selectedItem = items[index]

    return selectedItem?.buttonLabel ?? children ?? placeholder
  }, [items, selectedIndex, internalIndex])

  const handleButtonClick = () => {
    const didMenuOpenChangeCancel = onMenuOpenChange?.(!menuOpen) === false
    if (!menuOpen && didMenuOpenChangeCancel) {
      // The handler returned false, cancelling the open action
      return
    }

    setMenuOpen((prev) => !prev)
    onMenuOpenChange?.(!menuOpen)
    onButtonClick?.()
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setInternalIndex(index)
    setMenuOpen(false)
    onMenuOpenChange?.(false)
  }

  return (
    <MenuPositioningWrapper ref={containerRef} marginLeft={-5}>
      <Tooltip
        showArrow
        content={tooltipLabel}
        positioning={{ placement: 'top' }}
        disabled={!hasValue(tooltipLabel)}
      >
        <TextButton
          ref={buttonRef}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          leftIconProps={leftIconProps}
          rightIconProps={rightIconProps}
          onLinkClick={handleButtonClick}
          variant={variant}
          hoverChanged={hoverChanged}
        >
          {buttonLabel}
        </TextButton>
      </Tooltip>
      {menuOpen && items.length > 0 && (
        <Menu
          anchorRef={castRef(buttonRef)}
          items={items}
          onSelect={handleSelect}
          options={menuOptions}
          header={header}
        />
      )}
    </MenuPositioningWrapper>
  )
}
